/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const AddDepartmentDialog = ({ 
  open, 
  onClose, 
  onSuccess, 
  prefilledName = '',
  title = 'Add New Department'
}) => {
  const [departmentData, setDepartmentData] = useState({
    department_name: prefilledName,
    department_code: '',
    ouc_code: ''
  });
  const [loading, setLoading] = useState(false);

  // Update prefilled name when prop changes
  React.useEffect(() => {
    setDepartmentData(prev => ({
      ...prev,
      department_name: prefilledName
    }));
  }, [prefilledName]);

  const handleCreate = async () => {
    // Validation
    if (!departmentData.department_name.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    if (!departmentData.department_code.trim()) {
      toast.error('Department code is required');
      return;
    }
    
    // Check for lowercase letters (not allowed)
    if (/[a-z]/.test(departmentData.department_code)) {
      toast.error('Department code cannot contain lowercase letters');
      return;
    }
    
    if (!departmentData.ouc_code.trim() || departmentData.ouc_code.length !== 6) {
      toast.error('OUC code must be exactly 6 digits');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/departments', departmentData);
      toast.success('Department created successfully!');
      
      // Reset form
      setDepartmentData({
        department_name: '',
        department_code: '',
        ouc_code: ''
      });
      
      // Call success callback with new department data
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to create department:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create department';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setDepartmentData({
      department_name: prefilledName,
      department_code: '',
      ouc_code: ''
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {prefilledName && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.contrastText">
              💡 This department name was entered by a user in their application. 
              You can edit it to make it proper or correct any issues.
            </Typography>
          </Box>
        )}
        
        <TextField
          fullWidth
          margin="normal"
          label="Department Name"
          value={departmentData.department_name}
          onChange={(e) => setDepartmentData({
            ...departmentData,
            department_name: e.target.value
          })}
          required
          helperText="Full name of the department"
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="Department Code"
          value={departmentData.department_code}
          onChange={(e) => {
            // Convert lowercase to uppercase, allow special characters
            const value = e.target.value.replace(/[a-z]/g, char => char.toUpperCase());
            setDepartmentData({
              ...departmentData,
              department_code: value
            });
          }}
          required
          helperText="Uppercase letters, numbers, and special characters allowed (no lowercase)"
        />
        
        <TextField
          fullWidth
          margin="normal"
          label="OUC Code"
          value={departmentData.ouc_code}
          onChange={(e) => setDepartmentData({
            ...departmentData,
            ouc_code: e.target.value.replace(/\D/g, '').slice(0, 6)
          })}
          inputProps={{ maxLength: 6 }}
          required
          helperText="Must be exactly 6 digits"
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleCreate} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Department'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDepartmentDialog;
